/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: firewallSettingsApi.ts
 🗓️ Created: 2025-12-06
 🧩 Description:
    API client for Firewall Settings module
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type {
  FirewallSettings,
  FirewallSettingsFormData,
  FirewallSettingsResponse,
} from '../types';

/**
 * Fetch firewall settings
 */
export async function fetchFirewallSettings(): Promise<FirewallSettings> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/settings'),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch firewall settings');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Update firewall settings
 */
export async function updateFirewallSettings(
  data: FirewallSettingsFormData
): Promise<FirewallSettings> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/settings'),
    {
      method: 'PUT',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update firewall settings');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Get service statuses (firewall and fail2ban)
 */
export async function getServiceStatuses(): Promise<{
  firewallEnabled: boolean;
  intrusionDetectionEnabled: boolean;
}> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/settings/services/status'),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch service statuses');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Control firewall service
 */
export async function controlFirewallService(
  action: 'start' | 'stop' | 'restart'
): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/settings/services/firewall/${action}`),
    {
      method: 'POST',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to ${action} firewall service`);
  }
}

/**
 * Control fail2ban service
 */
export async function controlFail2BanService(
  action: 'start' | 'stop' | 'restart'
): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/settings/services/fail2ban/${action}`),
    {
      method: 'POST',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to ${action} fail2ban service`);
  }
}
